import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Layout } from '@/components/layout'
import { ProfileForm } from './components/ProfileForm'
import { PasswordChangeForm } from './components/PasswordChangeForm'
import { TwoFactorSettings } from './components/TwoFactorSettings'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function ProfilePage() {
  const { t } = useTranslation()
  const canUpdateProfile = useHasPermission('profile.update')
  const canManage2FA = useHasPermission('auth.2fa.manage')

  // If user has no permissions, show access denied message
  const hasAnyPermission = canUpdateProfile || canManage2FA
  if (!hasAnyPermission) {
    return (
      <Layout>
        <div className="border-b bg-muted/30">
          <div className="container mx-auto px-6 py-12">
            <div className="max-w-3xl space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-foreground">
                {t('profile.title', 'Profiel')}
              </h1>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-muted-foreground">
              {t('profile.noAccess', 'U heeft geen toegang tot deze pagina.')}
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-3xl space-y-4">
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              {t('profile.title', 'Profiel')}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {t('profile.description', 'Beheer uw profielgegevens en accountinstellingen.')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.settings', 'Accountinstellingen')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs
                defaultValue={
                  canUpdateProfile
                    ? 'profile'
                    : canManage2FA
                      ? 'security'
                      : 'profile'
                }
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  {(canUpdateProfile) && (
                    <TabsTrigger value="profile">
                      {t('profile.personalInfo', 'Persoonlijke gegevens')}
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="password">{t('profile.password', 'Wachtwoord')}</TabsTrigger>
                  {canManage2FA && (
                    <TabsTrigger value="security">{t('profile.security', '2FA')}</TabsTrigger>
                  )}
                </TabsList>

                {(canUpdateProfile) && (
                  <TabsContent value="profile" className="space-y-6">
                    <ProfileForm />
                  </TabsContent>
                )}

                <TabsContent value="password" className="space-y-6">
                  <PasswordChangeForm />
                </TabsContent>

                {canManage2FA && (
                  <TabsContent value="security" className="space-y-6">
                    <TwoFactorSettings />
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  )
}
